import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import login from '@/store/login'
import currentRestaurant from '@/store/currentRestaurant'
import currentProvider from '@/store/currentProvider'

Vue.use(Vuex)

const sortDataByName = function (data) {
  return data.sort((a, b) => {
    if (a.name > b.name) return 1
    if (a.name < b.name) return -1
    return 0
  })
}

export default new Vuex.Store({
  plugins: [
    createPersistedState({
      key: process.env.VUE_APP_STORE_KEY
    })
  ],
  modules: {
    login,
    currentRestaurant,
    currentProvider
  },
  state: {
    version: process.env.VUE_APP_VERSION || '0',
    session: {
      token: null,
      user: {
        email: '',
        uuid: '',
        language: ''
      }
    },
    restaurant: null,
    restaurants: null,
    area: null,
    areas: null,
    prospects: [],
    units: [],
    categories: [],
    sections: [],
    userProvider: null,
    isLoading: false,
    ordersQuantity: [],
    total_sales: null,
    amountAvailable: null
  },
  mutations: {
    setSession (state, data) {
      const { user, jwt } = data
      const { email, uuid, provider } = user

      state.session.token = jwt
      state.session.user = { email, uuid, provider }
    },
    setRestaurant (state, data) {
      state.restaurant = data
      state.session.restaurant = data.uuid
    },
    setRestaurants (state, data) {
      state.restaurants = sortDataByName(data)
    },
    setArea (state, data) {
      state.area = data
    },
    setAreas (state, data) {
      state.areas = sortDataByName(data)
    },
    setAmountAvailable (state, amount) {
      state.amountAvailable = Number(amount)
    },
    updateAreas (state, area) {
      const index = state.areas.findIndex(a => a.id === area.id)
      const areas = state.areas
      if (index < 0) {
        areas.push(area)
      } else {
        areas[index] = area
      }
      state.areas = areas
    },
    clearToken (state) {
      state.session = {
        token: null,
        user: {
          email: '',
          uuid: ''
        },
        restaurant: ''
      }
    },
    clearRestaurant (state) {
      state.restaurant = null
    },
    clearRestaurants (state) {
      state.restaurants = null
    },
    updateRestaurants (state, restaurant) {
      const index = state.restaurants.findIndex(r => r.id === restaurant.id)
      const restaurants = state.restaurants
      if (index < 0) {
        restaurants.push(restaurant)
      } else {
        restaurants[index] = restaurant
      }
      state.restaurant = restaurant
      state.restaurants = restaurants
    },
    clearArea (state) {
      state.area = null
    },
    clearAreas (state) {
      state.areas = null
    },
    setUnits (state, data) {
      state.units = sortDataByName(data)
    },
    clearUnits (state) {
      state.units = []
    },
    setCategories (state, data) {
      state.categories = sortDataByName(data)
    },
    clearCategories (state) {
      state.categories = []
    },
    setSections (state, data) {
      state.sections = sortDataByName(data)
    },
    clearSections (state) {
      state.sections = []
    },
    setUserprovider (state) {
      state.userProvider = true
    },
    clearUserprovider (state) {
      state.userProvider = null
    },
    changeLoading (state, loading) {
      state.isLoading = loading
    },
    setOrdersQuantity (state, data) {
      state.ordersQuantity = data
    },
    setTotalSales (state, data) {
      state.total_sales = data
    },
    setVersion (state, data) {
      state.version = data
    }
  },
  actions: {
    logout: async function ({ commit, dispatch }) {
      commit('changeLoading', false)
      commit('clearToken')
      commit('clearRestaurant')
      commit('clearArea')
      commit('clearRestaurants')
      commit('clearAreas')
      commit('clearUnits')
      commit('clearCategories')
      commit('clearSections')
      await dispatch('login/signOut')
      await dispatch('currentRestaurant/reset')
      await dispatch('currentProvider/reset')
      commit('clearUserprovider')
    },
    getUnits: async function ({ commit }) {
      const response = await Vue.prototype.$api.get(`/v1/units`)
      commit('setUnits', response.data)
    },
    getSections: async function ({ commit, state }) {
      const response = await Vue.prototype.$api.get(`/v1/restaurants/${state.restaurant.uuid}/sections`)
      commit('setSections', response.data)
    },
    getAreas: async function ({ commit, state }, restaurantId) {
      const response = await Vue.prototype.$api.get(`/v1/restaurants/${restaurantId}/areas`)
      await commit('setAreas', response.data)
    },
    getCategories: async function ({ commit, state }) {
      const response = await Vue.prototype.$api.get(`/v1/categories`)
      commit('setCategories', response.data)
    },
    selectRestaurant: async function ({ commit, dispatch, state }, restaurant) {
      await commit('setRestaurant', restaurant)
      await dispatch('getAreas', restaurant.uuid)
      const activeArea = state.area
      const selectedArea = state.areas.find(area => new RegExp(activeArea.name, 'gi').test(area.name)) || state.areas[0]
      commit('setArea', selectedArea)
    },
    getOrdersQuantity: async function ({ commit }) {
      const ordersQuantity = await Vue.prototype.$api.get('/v1/orders/number/orders-by-status')
      commit('setOrdersQuantity', ordersQuantity)
    }
  },
  getters: {
    filteredUnits (state) {
      const unitsAllowed = [
        'm',
        'cm',
        'g',
        'kg',
        'oz',
        'l',
        'ml',
        'glass',
        'cup',
        'cop',
        'oza',
        'ozi',
        'teaspoon',
        'tablespoon',
        'pz',
        'unit'
      ]
      return state.units.filter(unit => unitsAllowed.includes(unit.code)) || []
    },
    provider (state) {
      return state.session.user.provider
    },
    restaurant: state => { return state.currentRestaurant.data },
    areas: state => { return state.currentRestaurant.areas.data },
    allowedAreas: (state, getters) => {
      const allowedAreas = getters.permissions['areas-allowed'] || []
      return getters.areas.filter(area => allowedAreas.includes(area.id))
    },
    total_sales: state => { return state.total_sales },
    permissions: state => state.login.permissions
  }
})
